import React from 'react';
import {
    formatDate,
    getFirstPreviewImage,
    openCampaignCardAction,
    truncateString,
} from '../../../../../shared/functions/Functions';
import { Campaign, ILinkedCampaigns } from '../../../../../model/campaign/Campaign';
import { useTranslation } from 'react-i18next';
import { CampaignCardActionButtons } from '../../../dashboard/components/CampaignCardActionButtons';
import { AssignedUserTooltip } from '../../../../../shared/components/AssignedUserTooltip';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { useNavigate } from 'react-router-dom';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import CampaignStatusBadge from '../../../../../shared/components/CampaignStatusBadge';

interface IDashboardLinkedListItemProps {
    campaign: ILinkedCampaigns;
    displayModalAction?: (campaign?: Campaign | ILinkedCampaigns, modalType?: string) => void;
    addToShortcuts?: (id: number) => void;
    isLinkedCampOnDash?: boolean;
}

export function DashboardLinkedListItem({
    campaign,
    displayModalAction,
    addToShortcuts,
    isLinkedCampOnDash,
}: IDashboardLinkedListItemProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const title = campaign?.title ?? '-';
    const truncatedTitle = truncateString(title, 25);
    const isTruncated = title.length > 25;
    const isGeneralClient = useSelector((state: IAllStates) => state.auth?.user?.isGeneralClient);

    return (
        <tr className="border-bottom">
            <td className="text-nowrap py-2">
                <div className="d-flex">
                    <div
                        role="button"
                        className="cursor-pointer mr-2"
                        onClick={() => openCampaignCardAction(navigate, campaign, isGeneralClient)}
                    >
                        {getFirstPreviewImage(
                            'style_uploaded_image',
                            campaign?.campaignImages,
                            'campaign-icon default-radius'
                        )}
                    </div>
                    <div>
                        <div className="d-flex align-items-center mb-1">
                            <TooltipOverlay content={isTruncated ? title : ''}>
                                <h6
                                    className={`mb-0 mx-1 cursor-pointer color-black ${
                                        isLinkedCampOnDash ? 'textHoverStrong' : ''
                                    }`}
                                    onClick={() => openCampaignCardAction(navigate, campaign, isGeneralClient)}
                                >
                                    {truncatedTitle}
                                </h6>
                            </TooltipOverlay>
                        </div>
                        <div className="d-flex align-items-center">
                            <CampaignStatusBadge status={campaign?.status} phase={campaign?.phase} />
                            {!!campaign?.approvalStatus && (
                                <div className="mx-2">
                                    <TooltipOverlay content={t(`general.campaign_status.${campaign?.approvalStatus}`)}>
                                        <img
                                            src={`/icons/approval-status/${campaign?.approvalStatus}.svg`}
                                            alt="approval-status"
                                            className="icon-sm"
                                        />
                                    </TooltipOverlay>
                                </div>
                            )}
                            {campaign?.publishFrom && campaign?.publishTo && (
                                <p className="mb-0 font-12 ml-2 text-primary">
                                    {formatDate(campaign?.publishFrom as string)} -{' '}
                                    {formatDate(campaign?.publishTo as string)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </td>
            <td className="text-center">{campaign?.influencersBooked ?? '0'}</td>
            <td className="text-center">{campaign?.usedPostings ?? '0'}</td>
            <td className="text-center">
                {campaign?.reportingSummary?.reach && campaign?.reportingSummary?.reach > 0
                    ? priceSplitter(campaign.reportingSummary.reach)
                    : '-'}
            </td>
            <td className="text-center">
                {campaign?.reportingSummary?.engagementRate && campaign?.reportingSummary?.engagementRate > 0
                    ? `${campaign.reportingSummary.engagementRate.toLocaleString('sr-RS')} %`
                    : '-'}
            </td>
            <td className="text-center">
                <div className="d-flex align-items-center justify-content-center">
                    {campaign?.assignedUsers?.slice(0, 4).map(user => (
                        <TooltipOverlay
                            className="justify-content-center"
                            key={user?.id}
                            content={`${user.firstName} ${user.lastName}`}
                            style={{ marginLeft: `-1rem` }}
                        >
                            <CustomImageHandler
                                altTag="Campaign placeholder"
                                thumbnailPath={user?.profilePhoto?.thumbnail}
                                photoPath={user?.profilePhoto?.path}
                                classes="campaign-card-img user-img-rounded"
                                name={user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                            />
                        </TooltipOverlay>
                    ))}
                    <div style={{ marginLeft: `-12px` }}>
                        <AssignedUserTooltip numberVisibleUsers={4} users={campaign?.assignedUsers} />
                    </div>
                </div>
            </td>
            <td className="text-center">
                <div className="d-flex align-items-center justify-content-center">
                    {!!campaign?.partnerCompanies?.length
                        ? campaign?.partnerCompanies?.slice(0, 2).map(user => (
                              <TooltipOverlay
                                  className="justify-content-center"
                                  key={user?.id}
                                  content={user.displayName ?? ''}
                                  style={{ marginLeft: `-1rem` }}
                              >
                                  <CustomImageHandler
                                      altTag="Campaign placeholder"
                                      thumbnailPath={user?.profilePhoto?.thumbnail}
                                      photoPath={user?.profilePhoto?.path}
                                      classes="campaign-card-img user-img-rounded"
                                      name={
                                          user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
                                      }
                                  />
                              </TooltipOverlay>
                          ))
                        : '-'}
                    <div style={{ marginLeft: `-12px` }}>
                        <AssignedUserTooltip numberVisibleUsers={2} users={campaign?.partnerCompanies} />
                    </div>
                </div>
            </td>
            <td>
                <CampaignCardActionButtons
                    campaign={campaign}
                    isTableBody
                    displayModalAction={displayModalAction}
                    addToShortcuts={addToShortcuts}
                    isActionVisible={isLinkedCampOnDash}
                />
            </td>
        </tr>
    );
}
